import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import BeforeAfter from "../components/BeforeAfter";
import CTABanner from "../components/CTABanner";
import { useSEO } from "../hooks/useSEO";
import { realisations, categories, beforeAfter } from "../data/realisations";

export default function Realisations() {
  const [filter, setFilter] = useState("Tous");
  useSEO({
    title: "Nos réalisations — Avant / après jardinage et aménagement | Embèk Group",
    description:
      "Découvrez les réalisations d'Embèk Group : entretien de jardins, taille de haies, pelouses, aménagement paysager, nettoyage extérieur et création de jardins.",
  });

  const filtered = filter === "Tous" ? realisations : realisations.filter((r) => r.category === filter);

  return (
    <>
      <PageHeader
        eyebrow="Nos réalisations"
        title="Des extérieurs transformés"
        description="Entretien, taille, pelouses, aménagement, nettoyage, création : un aperçu de notre savoir-faire. Photos de démonstration à remplacer par vos chantiers."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          {/* Avant / Après */}
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-forest">Avant / Après</h2>
          <p className="mt-2 text-sm text-coal-muted font-light">Glissez le curseur pour comparer.</p>
          <div className="mt-8 grid md:grid-cols-2 gap-8">
            {beforeAfter.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.1}>
                <BeforeAfter before={b.before} after={b.after} title={b.title} category={b.category} />
              </Reveal>
            ))}
          </div>

          {/* Filtres */}
          <div className="mt-20 flex flex-wrap gap-2.5" data-testid="realisations-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`filter-${c.toLowerCase().replace(/ /g, "-")}`}
                className={`px-5 py-2.5 rounded-full text-[13px] font-semibold tracking-wide transition-all duration-300 ${
                  filter === c
                    ? "bg-forest text-sand shadow-lg shadow-forest/20"
                    : "bg-white text-forest/70 border border-forest/15 hover:border-forest/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Galerie */}
          <motion.div layout className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="realisations-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((r) => (
                <motion.figure
                  layout
                  key={r.id}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="group relative rounded-3xl overflow-hidden aspect-[4/3] bg-forest/5"
                  data-testid={`realisation-card-${r.id}`}
                >
                  <img
                    src={r.image}
                    alt={r.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/80 via-forest-dark/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                  <figcaption className="absolute bottom-0 left-0 right-0 p-6 translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-moss font-semibold">{r.category}</span>
                    <p className="mt-1 font-serif text-xl text-sand">{r.title}</p>
                  </figcaption>
                </motion.figure>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
